import React, { FC, forwardRef, HTMLAttributes, ReactNode, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { TIcons } from '../../type/icons-type';
import Item from './Item';
import List from './List';

interface INavigationLineProps {
	className?: string;
}
export const NavigationLine: FC<INavigationLineProps> = ({ className }) => {
	return <hr className={classNames('navigation-line', className)} />;
};
NavigationLine.propTypes = {
	className: PropTypes.string,
};
NavigationLine.defaultProps = {
	className: undefined,
};

interface INavigationTitleProps extends HTMLAttributes<HTMLSpanElement> {
	className?: string;
	children: ReactNode;
}
export const NavigationTitle: FC<INavigationTitleProps> = ({ className, children, ...props }) => {
	return (
		<li className='navigation-item'>
			{/* eslint-disable-next-line react/jsx-props-no-spreading */}
			<span className={classNames('navigation-title', className)} {...props}>
				{children}
			</span>
		</li>
	);
};
(NavigationTitle as any).propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
NavigationTitle.defaultProps = {
	className: undefined,
};

interface INavigationProps {
	horizontal?: boolean;
	menu: {
		[key: string]: {
			id?: string | number;
			text?: string;
			path?: string;
			icon?: TIcons;
			isDisable?: boolean;
			subMenu?: {
				[key: string]: {
					id?: string | number;
					text?: string;
					path?: string;
					icon?: TIcons;
					isDisable?: boolean;
				};
			} | null;
		};
	};
	id: string;
	className?: string;
}
const Navigation = forwardRef<HTMLElement, INavigationProps>(
	({ menu, horizontal, id, className, ...props }, ref) => {
		const [activeItem, setActiveItem] = useState(undefined);
        const [aksesMenu, setAksesMenu] = useState<Record<string, boolean>>({});

        useEffect(() => {
            const storedData = sessionStorage.getItem('data');
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                setAksesMenu(parsedData.aksesMenu || {});
            }
        }, []);

		const { t } = useTranslation('menu');

		function fillMenu(
			data:
				| {
						id?: string | number;
						text?: string;
						path?: string;
						icon?: TIcons;
						isDisable?: boolean;
						subMenu?:
							| {
									id?: string | number;
									text?: string;
									path?: string;
									icon?: TIcons;
									isDisable?: boolean;
							  }[]
							| undefined;
				  }[]
				| any,
			parentId: string,
			rootId: string,
			isHorizontal: boolean | undefined,
			isMore: boolean | undefined,
		) {
            return Object.keys(data).map((itemKey) => {
                    const menuItem = data[itemKey];

                    // Buat key unik untuk pengecekan
                    const keyCheck =
                        (menuItem.path || '').replace(/-/g, '') || menuItem.id?.toString() || '';

                    // 🚫 Hide menu jika aksesMenu[key] === false
                    if (
                        aksesMenu[menuItem.id as string] === false ||
                        aksesMenu[menuItem.path as string] === false ||
                        aksesMenu[keyCheck] === false
                    ) {
                        return null;
                    }

                    return menuItem.path ? (
                        <Item
                            key={menuItem.id}
                            rootId={rootId}
                            id={menuItem.id}
                            title={menuItem.text}
                            icon={menuItem.icon}
                            to={`${menuItem.path}`}
                            parentId={parentId}
                            isHorizontal={isHorizontal}
                            setActiveItem={setActiveItem}
                            activeItem={activeItem}
                            notification={menuItem.notification}
                            hide={menuItem.hide}
                        >
                            {!!menuItem.subMenu &&
                                fillMenu(menuItem.subMenu, menuItem.id, rootId, isHorizontal, undefined)}
                        </Item>
                    ) : (
                        !isMore &&
                        !isHorizontal && (
                            <NavigationTitle key={menuItem.id}>
                                {t(menuItem.text) as ReactNode}
                            </NavigationTitle>
                        )
                    );
                });
		}

		return (
			// @ts-ignore
			// eslint-disable-next-line react/jsx-props-no-spreading
			<nav ref={ref} aria-label={id} className={className} {...props}>
				<List id={id} horizontal={horizontal}>
					{fillMenu(menu, id, id, horizontal, undefined)}
					{horizontal && (
						<Item
							rootId={`other-${id}`}
							title={t('More') as string}
							icon='MoreHoriz'
							isHorizontal
							isMore>
							{fillMenu(menu, `other-${id}`, `other-${id}`, false, true)}
						</Item>
					)}
				</List>
			</nav>
		);
	},
);
Navigation.displayName = 'Navigation';
Navigation.propTypes = {
	horizontal: PropTypes.bool,
	// @ts-ignore
	menu: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		text: PropTypes.string,
		path: PropTypes.string,
		icon: PropTypes.string,
		isDisable: PropTypes.bool,
		subMenu: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				text: PropTypes.string,
				path: PropTypes.string,
				icon: PropTypes.string,
				isDisable: PropTypes.bool,
			}),
		),
	}).isRequired,
	id: PropTypes.string.isRequired,
	className: PropTypes.string,
};
Navigation.defaultProps = {
	horizontal: false,
	className: undefined,
};

export default Navigation;
